import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { green, red } from '@mui/material/colors'
import Circle from '@mui/icons-material/Circle'
import Icon from 'components/Icon'
import styles from './EmployeesTab.module.scss'
import Tooltip from '@mui/material/Tooltip'
import { useCopyToClipboard } from 'containers/VerticalPage/common/hooks'
import { NOT_AVAILABLE, UNKNOWN } from 'utils/constants'
import { parse } from 'date-fns'
import { DATE_FORMAT_MMM_YYYY, formattedDate } from 'utils/formatters'

interface Item {
  experiences?: {
    name: string
    jobLocation: string
    jobRole: string
    jobDepartment: string
    startDate: string
    endDate: string
    jobDesc: string
    currentJob: boolean
    isExecutiveRole: boolean
    canonicalUrl: string
    status: string
  }[]
  name: string
  jobLocation: string
  jobRole: string
  jobDepartment: string
  startDate: string
  endDate: string
  jobDesc: string
  currentJob: boolean
  isExecutiveRole: boolean
  status: string
  canonicalUrl: string
}

interface LinkedInProfileCellProps {
  url: string
}

const LinkedInProfileCell = ({ url }: LinkedInProfileCellProps) => {
  const { handleCopy, isLinkCopied } = useCopyToClipboard()

  if (!url) {
    return <Typography>{NOT_AVAILABLE}</Typography>
  }

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleCopy(url)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        component='a'
        href={url}
        onClick={handleLinkClick}
        style={{
          color: 'rgba(34, 87, 197, 1)',
          cursor: 'pointer',
        }}
      >
        LinkedIn Profile
      </Typography>
      <Tooltip title={isLinkCopied ? 'Link copied' : 'Copy link'} arrow>
        <Box
          onClick={handleCopyClick}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            p: 0.5,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {isLinkCopied ? <Icon name='done' size='small' /> : <Icon name='copy' size='small' />}
        </Box>
      </Tooltip>
    </Box>
  )
}

const DescriptionCell = ({ description }: { description: string }) => {
  const [showFullDescription, setShowFullDescription] = useState(false)

  if (!description || description.length <= 56) {
    return <Typography>{description}</Typography>
  }

  return (
    <Box>
      <Typography>{showFullDescription ? description : `${description.substring(0, 56)}...`}</Typography>
      <Button
        onClick={() => setShowFullDescription(!showFullDescription)}
        sx={{
          textTransform: 'none',
          color: 'rgba(34, 87, 197, 1)',
          fontFamily: 'Source Sans Pro',
          fontSize: '16px',
          fontWeight: 400,
          p: 0,
          '.MuiTouchRipple-root': {
            display: 'none',
          },
          '&:hover': {
            background: 'transparent',
          },
        }}
      >
        {showFullDescription ? 'View less' : 'View more'}
      </Button>
    </Box>
  )
}

const calculatePeriod = (start: string, end: string) => {
  let period = ''
  if (!start || !end) return period

  const startTimestamp = parse(start, 'yyyy/MM/dd', new Date())
  const endTimestamp = parse(end, 'yyyy/MM/dd', new Date())

  const startDate = new Date(startTimestamp)
  const endDate = new Date(endTimestamp)

  const diffMilliseconds = endDate.getTime() - startDate.getTime()
  const diffMonths = diffMilliseconds / (1000 * 60 * 60 * 24 * 30.44)

  const years = Math.floor(diffMonths / 12)
  const months = Math.floor(diffMonths % 12)

  if (years > 0) {
    period += `${years} yr `
  }
  if (months > 0) {
    period += `${months} mos`
  }

  return period.trim()
}

const defaultColumnIds = [
  'Name',
  'Location',
  'JobDepartment',
  'JobRole',
  'startDate',
  'endDate',
  'Period',
  'JobDesc',
  'Status',
  'Canonical Url',
]

const useEmployeesColumns = (displayColumnIds: string[] = defaultColumnIds) => {
  const hasExperiences = (item: Item) => {
    return item?.experiences !== undefined && item.experiences?.length > 0
  }

  return [
    {
      id: 'Name',
      header: 'Name',
      grow: 1,
      size: 120,
      accessorFn: (item: Item) => {
        const name = hasExperiences(item) ? item.experiences![0]?.name : item?.name
        let icon = null
        let tooltipTitle = ''

        if (hasExperiences(item)) {
          const isExecutive = item.experiences!.some(experience => experience.isExecutiveRole)
          if (isExecutive) {
            icon = <Icon name='businessman' size='regular' />
            tooltipTitle = 'Executive Employee'
          }
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{name}</Typography>
            {icon && (
              <Tooltip title={tooltipTitle} classes={{ tooltip: styles.customMuiTooltip }} arrow>
                <Box>{icon}</Box>
              </Tooltip>
            )}
          </Box>
        )
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {},
      },
    },
    {
      id: 'Location',
      header: 'Location',
      grow: 1,
      size: 120,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0]?.jobLocation : item?.jobLocation
        return field || UNKNOWN
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'JobDepartment',
      header: 'Department',
      grow: 1,
      size: 150,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0]?.jobDepartment : item?.jobDepartment
        return field || NOT_AVAILABLE
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'JobRole',
      header: 'Job Role',
      grow: 1,
      size: 220,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0]?.jobRole : item?.jobRole
        return field
          ? field.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
          : NOT_AVAILABLE
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'startDate',
      header: 'Start Date',
      size: 110,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0].startDate : item?.startDate
        return field ? formattedDate(field, DATE_FORMAT_MMM_YYYY) : NOT_AVAILABLE
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'endDate',
      header: 'End Date',
      size: 110,
      accessorFn: (item: Item) => {
        const startDate = hasExperiences(item) ? item.experiences![0].startDate : item?.startDate
        const field = hasExperiences(item) ? item.experiences![0].endDate : item?.endDate
        if (!field && startDate) return 'Present'
        return field ? formattedDate(field, DATE_FORMAT_MMM_YYYY) : NOT_AVAILABLE
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'Period',
      header: 'Period',
      size: 110,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item)
          ? calculatePeriod(item.experiences?.[0]?.startDate || '', item.experiences?.[0]?.endDate || '')
          : calculatePeriod(item?.startDate, item?.endDate)
        return field
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'JobDesc',
      header: 'Description',
      grow: 1,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0].jobDesc : item?.jobDesc
        return <DescriptionCell description={field} />
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'Status',
      header: 'Status',
      grow: 1,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0].status : item.status
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Circle sx={{ color: field === 'Active' ? green[500] : red[500], mr: 1, fontSize: 10 }} />
            <Typography>{field}</Typography>
          </Box>
        )
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
    {
      id: 'Canonical Url',
      header: 'LinkedIn Profile URL',
      grow: 1,
      accessorFn: (item: Item) => {
        const field = hasExperiences(item) ? item.experiences![0]?.canonicalUrl : item?.canonicalUrl
        return <LinkedInProfileCell url={field} />
      },
      muiTableHeadCellProps: {
        sx: {
          color: '#7E8BA6',
          borderBottom: 'none',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          borderInlineEnd: '1px solid #DFDFDF',
        },
      },
    },
  ].filter(column => displayColumnIds.includes(column.id))
}

export default useEmployeesColumns
